/**
 * WordPress dependencies
 */
import { BaseControl, CheckboxControl } from '@safe-wordpress/components';
import { useSelect } from '@safe-wordpress/data';
import { _x } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { map, sortBy, without } from 'lodash';
import { store as NAB_DATA } from '@nab/data';

/**
 * Internal dependencies
 */
import './style.scss';
import { StylizedSelectControl } from '../stylized-select-control';
import { getPostTypeExceptions } from '../../../packages/experiment-library/custom-post-type/get-post-type-exceptions';

type Attrs = {
	readonly postTypes: ReadonlyArray< string >;
	readonly isWooCommerceEnabled: boolean;
	readonly includeWooCommerceOrderInfo: boolean;
};

export type AiPrivacySettingsProps = {
	readonly disabled?: boolean;
	readonly attributes: Attrs;
	readonly setAttributes: ( attrs: Partial< Attrs > ) => void;
};

export const AiPrivacySettings = ( {
	disabled = false,
	attributes,
	setAttributes,
}: AiPrivacySettingsProps ): JSX.Element => {
	const isWooCommercePluginActive = useSelect(
		( select ) =>
			select( NAB_DATA )
				.getActivePlugins()
				.includes( 'woocommerce/woocommerce.php' ),
		[]
	);

	const availablePostTypes = useAvailablePostTypes();
	const selectedPostTypes = availablePostTypes.filter( ( t ) =>
		attributes.postTypes.includes( t.value )
	);

	return (
		<div className="nab-ai-privacy-settings">
			<BaseControl
				id="nab-ai-privacy-settings__post-types"
				className="nab-ai-privacy-settings__post-types"
				label={ _x( 'Base Content', 'text', 'nelio-ab-testing' ) }
			>
				<StylizedSelectControl
					isMulti
					isDisabled={ disabled }
					options={ availablePostTypes }
					value={ selectedPostTypes }
					onChange={ ( postTypes ) =>
						setAttributes( {
							postTypes: map( postTypes, 'value' ),
						} )
					}
				/>
			</BaseControl>
			{ isWooCommercePluginActive && (
				<BaseControl
					id="nab-ai-privacy-settings__woocommerce"
					className="nab-ai-privacy-settings__woocommerce"
					label="WooCommerce"
				>
					<CheckboxControl
						label={ _x(
							'Include WooCommerce store in test suggestions',
							'command',
							'nelio-ab-testing'
						) }
						disabled={ disabled }
						checked={ attributes.isWooCommerceEnabled }
						onChange={ ( isWooCommerceEnabled ) =>
							setAttributes( {
								isWooCommerceEnabled,
								includeWooCommerceOrderInfo: true,
							} )
						}
					/>
					{ attributes.isWooCommerceEnabled && (
						<CheckboxControl
							label={ _x(
								'Share anonymized WooCommerce order data to improve test suggestions',
								'command',
								'nelio-ab-testing'
							) }
							disabled={ disabled }
							checked={ attributes.includeWooCommerceOrderInfo }
							onChange={ ( includeWooCommerceOrderInfo ) =>
								setAttributes( { includeWooCommerceOrderInfo } )
							}
						/>
					) }
				</BaseControl>
			) }
		</div>
	);
};

// =====
// HOOKS
// =====

const useAvailablePostTypes = () => {
	const { plugins, kinds } = useSelect(
		( select ) => ( {
			plugins: select( NAB_DATA ).getActivePlugins(),
			kinds: select( NAB_DATA ).getKindEntities(),
		} ),
		[]
	);

	const isWooCommerceEnabled = plugins.includes(
		'woocommerce/woocommerce.php'
	);
	const postTypeExceptions = without(
		getPostTypeExceptions( {
			isWooCommerceEnabled,
		} ),
		'page',
		'post'
	);

	const postTypes = kinds
		.filter( ( k ) => k.kind === 'entity' )
		.filter( ( t ) => ! t.name.startsWith( 'nab_' ) )
		.filter( ( t ) => ! postTypeExceptions.includes( t.name ) );
	return sortBy(
		postTypes.map( ( t ) => ( {
			label: t.label || t.labels.singular_name || t.name,
			value: t.name,
		} ) ),
		'label'
	);
};
